if (!customElements.get('product-form')) {
  // Load the configurator from this theme's own asset directory, including
  // when a configured product is first opened from a collection's Quick Buy.
  const optionsModuleUrl = document.currentScript?.src
    ? new URL('raceit-product-options.js', document.currentScript.src).href : null;
  let optionsModulePromise;
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      if (this.hasChildNodes()) this.init();
    }

    init() {
      this.form = this.querySelector('.js-product-form');
      if (this.form) {
        if (this.hasAttribute('data-raceit-product-options') && optionsModuleUrl && !customElements.get('raceit-product-options')) {
          optionsModulePromise ||= import(optionsModuleUrl).catch(() => null);
        }
        this.cartDrawer = document.querySelector('cart-drawer');
        this.initIncolorGeometrySelector();

        if (theme.settings.afterAtc !== 'no-js' || this.hasAttribute('data-quickpro-configured') || this.hasAttribute('data-galfer-configured') || this.hasAttribute('data-raceit-product-options') || this.hasAttribute('data-legacy-supplements')) {
          this.form.addEventListener('submit', this.handleSubmit.bind(this));
        }
      }
    }

    /**
     * Handles submission of the product form.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;

      evt.preventDefault();

      const extraOptions = [...(this.closest('.js-product') || this.closest('.js-quickbuy') || this.closest('.shopify-section') || this)
        .querySelectorAll('raceit-product-options')].find(element => element.dataset.formId === this.form.getAttribute('id'));
      if (extraOptions) {
        const error = extraOptions.validate?.() || (!extraOptions.ready ? { message: extraOptions.dataset.unavailableError } : null);
        if (error) {
          this.setAttribute('data-raceit-options-error', '');
          this.setErrorMsgState(error.message);
          error.control?.focus();
          return;
        }
        this.removeAttribute('data-raceit-options-error');
      }

      const geometryError = this.validateIncolorGeometry();
      if (geometryError) {
        this.setErrorMsgState(geometryError);
        const firstProfile = this.incolorGeometry?.querySelector('[data-incolor-geometry-profile]');
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.incolorGeometry?.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
        firstProfile?.focus({ preventScroll: true });
        return;
      }

      const quickProError = this.validateQuickProConfiguration();
      if (quickProError) {
        this.setErrorMsgState(quickProError.message);
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        quickProError.group.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
        quickProError.control?.focus({ preventScroll: true });
        return;
      }

      this.submitBtn = this.querySelector('[name="add"]');

      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();

      // Disable "Add to Cart" button until submission is complete.
      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');

      window.raceitCrankConfig?.syncForm(this.form);
      const formData = new FormData(this.form);
      let sections = 'cart-icon-bubble';
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sections);

      const productRoot = this.closest('.js-product')
        || this.closest('.js-quickbuy')
        || this.closest('.shopify-section')
        || document;
      const hyper2Option = productRoot.querySelector('[data-hyper2-integrated-option]:checked');
      const hyper2Selected = Boolean(hyper2Option);
      let hyper2VariantId = this.form.querySelector('[data-hyper2-upsell-variant]')?.value;
      if (hyper2Selected && !hyper2VariantId && window.raceitHyper2Upsell) {
        hyper2VariantId = await window.raceitHyper2Upsell.resolve(this.form).catch(() => '');
      }

      if (hyper2Selected && !hyper2VariantId) {
        this.setErrorMsgState(
          hyper2Option?.dataset.hyper2SizeError
            || 'Selecciona la anchura y longitud de potencia de Hyper2.0 antes de añadir al carrito.'
        );
        this.submitBtn.removeAttribute('aria-disabled');
        this.submitBtn.classList.remove('is-loading');
        return;
      }

      // Add-ons ride along as their own cart lines. This keeps their price and
      // fulfilment visible instead of relying on a fragile bundle discount.
      //
      // Three shapes, by how the line may be edited in the cart:
      //  - addOnVariantIds     free-standing lines the shopper may keep or drop
      //                        on their own.
      //  - lockedAddOnVariantIds  lines that only make sense attached to this
      //                        product — the handlebar spacer service.
      //  - supplementVariantIds  internal Quick Pro delta-priced products.
      //
      // The last two are bound to this line and submit identically, as bundle
      // 'component' lines. They keep separate names only because they are
      // collected from different controls. In a normal cart Shopify's Cart
      // Transform Function merges them into the parent line before the shopper
      // sees them; snippets/cart-items.liquid draws them as locked child rows
      // only if that merge does not apply.
      const addOnVariantIds = [];
      const lockedAddOnVariantIds = [];
      const supplementVariantIds = [];
      if (hyper2Selected) lockedAddOnVariantIds.push(hyper2VariantId);

      const galferAddon = [...productRoot.querySelectorAll('galfer-wheel-addon')]
        .find((addon) => addon.dataset.formId === this.form.getAttribute('id'));
      const galferVariantId = galferAddon?.getSelectedVariantId(formData.get('id'));
      if (galferVariantId) lockedAddOnVariantIds.push(galferVariantId);

      // Quick Pro configurator: every paid choice — wheels, power meter, bottom
      // bracket, handlebar, groupset — tags its control with the variant id of
      // the supplement that charges for it. Collect them all rather than just
      // the wheel select, and skip DISABLED groups: a conditional group that is
      // currently hidden must not bill for a part the shopper cannot see.
      productRoot.querySelectorAll('[data-qp-group]:not([hidden])').forEach((group) => {
        const input = group.querySelector('input[data-qp-input]:not([disabled])');
        const selected = group.querySelector('[data-qp-choice]:checked:not([disabled])');
        if (!input || !selected) return;
        // Trust the hidden line-item property: it is what submits. The selected
        // radio must agree before its supplement can be charged.
        if (selected.value === input.value && selected.dataset.qpSupplementVariant) {
          supplementVariantIds.push(selected.dataset.qpSupplementVariant);
        }
      });

      // productRoot, not this.form: in the Quick Buy drawer the add-on renders
      // in the scrollable options area while the form sits in the footer, so a
      // form-scoped lookup would miss it. The checkbox carries form="…" so the
      // line-item property still submits with the form either way.
      const spacerServiceOption = productRoot.querySelector('[data-handlebar-spacer-service]:checked');
      const spacerServiceVariantId = spacerServiceOption?.dataset.handlebarSpacerVariantId;
      // Keep the selected service linked to its handlebar in the cart. Its
      // visible Collective product stays unmerged so Shopify can discount it.
      if (spacerServiceVariantId) lockedAddOnVariantIds.push(spacerServiceVariantId);

      // Custom paint (Incolor / Quick Pro): the bike line carries
      // `Pintura personalizada: Sí`, the supplement pays for it and lives and
      // dies with the bike; the Cart Transform merges both into one line and
      // re-emits the property — see snippets/custom-paint-addon.liquid.
      const customPaintVariantId = productRoot
        .querySelector('[data-custom-paint]:checked:not([disabled])')?.dataset.customPaintVariantId;
      if (customPaintVariantId) lockedAddOnVariantIds.push(customPaintVariantId);
      const extraOptionVariantIds = extraOptions?.selectedVariantIds() || [];
      lockedAddOnVariantIds.push(...extraOptionVariantIds);

      // The bike field only matters when the spacer service is requested —
      // it must not block checkout for ordinary handlebar purchases.
      const standaloneSpacerBike = productRoot.querySelector('[data-standalone-spacer-bike]');
      if (spacerServiceOption || standaloneSpacerBike) {
        const spacerBikeInput = standaloneSpacerBike || productRoot.querySelector('[data-handlebar-spacer-bike]');
        if (spacerBikeInput && !spacerBikeInput.value.trim()) {
          this.setErrorMsgState(
            (spacerServiceOption || standaloneSpacerBike).dataset.handlebarSpacerBikeError
              || 'Indica para qué bici es antes de añadir al carrito.'
          );
          const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          spacerBikeInput.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
          spacerBikeInput.focus({ preventScroll: true });
          this.submitBtn.removeAttribute('aria-disabled');
          this.submitBtn.classList.remove('is-loading');
          return;
        }
      }

      const hasAdditionalItems = addOnVariantIds.length
        || lockedAddOnVariantIds.length
        || supplementVariantIds.length;
      const requestBody = hasAdditionalItems
        ? ProductForm.createMultiItemCartPayload(
          formData, addOnVariantIds, supplementVariantIds, lockedAddOnVariantIds, extraOptionVariantIds
        )
        : formData;
      const fetchRequestHeaders = {
        Accept: 'application/javascript',
        'X-Requested-With': 'XMLHttpRequest'
      };
      if (hasAdditionalItems) fetchRequestHeaders['Content-Type'] = 'application/json';

      const fetchRequestOpts = {
        method: 'POST',
        headers: fetchRequestHeaders,
        body: requestBody
      };

      try {
        const oldCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!oldCartResponse.ok) throw new Error(oldCartResponse.status);
        const oldCartData = await oldCartResponse.json();

        const response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
        const data = await response.json();
        let error = typeof data.description === 'string' ? data.description : data.message;
        if (data.errors && typeof data.errors === 'object') {
          error = Object.entries(data.errors).map((item) => item[1].join(', '));
        }
        // Shopify answers a burst of adds with an English "Please try again in
        // a few minutes"; say it in the shopper's language instead.
        if (response.status === 429 || data.status === 'too_many_requests') {
          error = theme.strings.cartTooManyAttempts || error;
        }

        if (data.status) this.setErrorMsgState(error);

        if (!response.ok) throw new Error(response.status);

        if (theme.settings.afterAtc === 'page' || theme.settings.afterAtc === 'no-js') {
          // Allow the tick animation to complete
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else {
          // Update cart icon count.
          ProductForm.updateCartIcon(data);

          // If item was added from Quick Add drawer, show "Added to cart" message.
          const quickAddDrawer = this.closest('quick-add-drawer');
          if (quickAddDrawer) quickAddDrawer.addedToCart();

          setTimeout(() => {
            // Update cart drawer contents.
            if (this.cartDrawer) {
              this.cartDrawer.renderContents(
                data,
                !quickAddDrawer && theme.settings.afterAtc === 'drawer'
              );
            } else if (window.location.pathname === theme.routes.cart) {
              const cartItems = document.querySelector('cart-items');
              if (cartItems) {
                if (cartItems.dataset.empty === 'true') {
                  window.location.reload();
                } else {
                  cartItems.refresh();
                }
              }
            }
          }, 700);
        }

        const newCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!newCartResponse.ok) throw new Error(newCartResponse.status);
        const newCartData = await newCartResponse.json();
        const addedItem = Array.isArray(data) ? data[0] : (data.items?.[0] || data);
        const addedVariantId = data.variant_id || addedItem.variant_id || addedItem.id;
        const addedQuantity = data.quantity || addedItem.quantity;
        const itemInOldCart = oldCartData.items.filter(
          (item) => item.variant_id === addedVariantId
        )[0];

        // Check if product was already in the cart
        if (itemInOldCart) {
          this.dispatchEvent(new CustomEvent('on:line-item:change', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId: addedVariantId,
              oldQuantity: itemInOldCart.quantity,
              newQuantity: (itemInOldCart.quantity === addedQuantity)
                ? itemInOldCart.quantity : addedQuantity
            }
          }));
        } else {
          this.dispatchEvent(new CustomEvent('on:cart:add', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId: addedVariantId
            }
          }));
        }
      } catch (error) {
        console.log(error); // eslint-disable-line
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg?.textContent
          }
        }));

        if (this.cartDrawer) this.cartDrawer.refresh(true);
      } finally {
        // Re-enable 'Add to Cart' button.
        this.submitBtn.classList.add('is-success');
        this.submitBtn.removeAttribute('aria-disabled');
        setTimeout(() => {
          this.submitBtn.classList.remove('is-loading');
          this.submitBtn.classList.remove('is-success');
        }, 1400);
      }
    }

    /**
     * Initializes the INCOLOR SSR geometry line-item property controls.
     */
    initIncolorGeometrySelector() {
      const geometryScope = this.closest('.js-product') || this.form;
      const geometrySelector = geometryScope.querySelector('[data-incolor-geometry-selector]');
      if (!geometrySelector || this.incolorGeometry === geometrySelector) return;
      this.incolorGeometry = geometrySelector;

      this.incolorGeometryTypes = Array.from(
        this.incolorGeometry.querySelectorAll('[data-incolor-geometry-type]')
      );
      this.incolorGeometryProfiles = Array.from(
        this.incolorGeometry.querySelectorAll('[data-incolor-geometry-profile]')
      );
      this.incolorGeometryTypes.forEach((input) => {
        input.disabled = false;
      });

      const syncGeometry = () => {
        const isCustom = this.incolorGeometryTypes.some(
          (input) => input.checked && input.value === 'Custom'
        );
        const customPanel = this.incolorGeometry.querySelector('[data-incolor-geometry-custom]');
        const customType = this.incolorGeometryTypes.find((input) => input.value === 'Custom');

        customPanel.hidden = !isCustom;
        customType?.setAttribute('aria-expanded', isCustom ? 'true' : 'false');
        this.incolorGeometryProfiles.forEach((input) => {
          input.disabled = !isCustom;
        });

        if (!isCustom) this.clearIncolorGeometryError();
        this.syncIncolorGeometryHighlights();
      };

      this.incolorGeometryTypes.forEach((input) => input.addEventListener('change', syncGeometry));
      this.incolorGeometryProfiles.forEach((input) => {
        input.addEventListener('change', () => {
          this.clearIncolorGeometryError();
          this.syncIncolorGeometryHighlights();
        });
      });

      const productRoot = this.closest('.js-product')
        || this.closest('.js-quickbuy')
        || this.closest('.shopify-section');
      productRoot?.addEventListener('on:variant:change', (event) => {
        const availableSizes = ['45', '48', '51', '54', '56', '58', '60'];
        const selectedSize = event.detail?.selectedOptions?.find(
          (value) => availableSizes.includes(String(value))
        );
        if (selectedSize) {
          this.incolorGeometry.dataset.currentSize = selectedSize;
          this.syncIncolorGeometryHighlights();
        }
      });

      syncGeometry();
    }

    /**
     * Highlights the selected custom profile and current frame size in the matrix.
     */
    syncIncolorGeometryHighlights() {
      if (!this.incolorGeometry) return;

      const selectedProfile = this.incolorGeometryProfiles.find((input) => input.checked);
      this.incolorGeometry.querySelectorAll('[data-incolor-profile-row]').forEach((row) => {
        row.classList.toggle(
          'is-selected-profile',
          Boolean(selectedProfile && row.dataset.incolorProfileRow === selectedProfile.dataset.incolorGeometryProfile)
        );
      });

      const currentSize = this.incolorGeometry.dataset.currentSize;
      this.incolorGeometry.querySelectorAll('[data-incolor-size]').forEach((cell) => {
        cell.classList.toggle('is-current-size', Boolean(currentSize && cell.dataset.incolorSize === currentSize));
      });

      // Mobile compact table: show only the group of the selected size
      if (currentSize) {
        this.incolorGeometry.querySelectorAll('[data-incolor-size-group]').forEach((group) => {
          group.hidden = group.dataset.incolorSizeGroup !== currentSize;
        });
      }

      const sizeBadge = this.incolorGeometry.querySelector('[data-incolor-size-badge]');
      if (sizeBadge && currentSize) {
        const label = sizeBadge.dataset.sizeLabel || '';
        sizeBadge.textContent = `${label} · ${currentSize}`;
        sizeBadge.hidden = false;
      }
    }

    /**
     * Clears the inline INCOLOR geometry validation state.
     */
    clearIncolorGeometryError() {
      if (!this.incolorGeometry) return;
      this.incolorGeometry.classList.remove('is-invalid');
      this.incolorGeometry.removeAttribute('aria-invalid');
      const error = this.incolorGeometry.querySelector('[data-incolor-geometry-error]');
      if (error) error.hidden = true;
      if (this.incolorGeometryWasInvalid) {
        this.incolorGeometryWasInvalid = false;
        this.setErrorMsgState();
      }
    }

    /**
     * Validates that Custom geometry includes one of the four required profiles.
     * @returns {string|false} Localized error message, or false when valid.
     */
    validateIncolorGeometry() {
      if (!this.incolorGeometry) return false;

      const isCustom = this.incolorGeometryTypes.some(
        (input) => input.checked && input.value === 'Custom'
      );
      const hasProfile = this.incolorGeometryProfiles.some((input) => input.checked && !input.disabled);
      if (!isCustom || hasProfile) {
        this.clearIncolorGeometryError();
        return false;
      }

      const error = this.incolorGeometry.querySelector('[data-incolor-geometry-error]');
      this.incolorGeometryWasInvalid = true;
      this.incolorGeometry.classList.add('is-invalid');
      this.incolorGeometry.setAttribute('aria-invalid', 'true');
      if (error) error.hidden = false;
      return this.incolorGeometry.dataset.requiredMessage;
    }

    /**
     * Validate all visible Quick Pro property groups. Their visible radios stay
     * outside the product form to avoid duplicate cart properties, so this is
     * the form-level equivalent of native radio constraint validation.
     * @returns {{message: string, group: HTMLElement, control: HTMLElement}|false}
     */
    validateQuickProConfiguration() {
      const productRoot = this.closest('.js-product')
        || this.closest('.js-quickbuy')
        || this.closest('.shopify-section')
        || document;
      const setup = productRoot.querySelector('[data-quickpro-setup]');
      if (!setup) return false;

      let firstInvalid = null;
      setup.querySelectorAll('[data-qp-group]:not([hidden])').forEach((group) => {
        const input = group.querySelector('input[data-qp-input]:not([disabled])');
        const selected = group.querySelector('[data-qp-choice]:checked:not([disabled])');
        const valid = Boolean(input?.value && selected?.value === input.value);
        group.classList.toggle('is-invalid', !valid);
        if (valid) {
          group.removeAttribute('aria-invalid');
        } else {
          group.setAttribute('aria-invalid', 'true');
          if (!firstInvalid) firstInvalid = group;
        }
      });

      if (!firstInvalid) return false;
      return {
        message: setup.dataset.qpRequiredMessage || 'Selecciona todas las opciones obligatorias antes de añadir al carrito.',
        group: firstInvalid,
        control: firstInvalid.querySelector('[data-qp-choice]:not([disabled])')
      };
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     */
    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) {
        cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
      }
    }

    /**
     * Convert a normal product form into Shopify's multi-item cart-add format,
     * so each selected add-on (Hyper 2.0 handlebar, Quick Pro wheel upgrade)
     * stays priced as its own product line.
     *
     * @param {FormData} formData - Product form data, including cart section requests.
     * @param {string|string[]} addOnVariantIds - Normal standalone add-on variant ID(s).
     * @param {string|string[]} supplementVariantIds - Internal supplement component variant ID(s).
     * @param {string|string[]} lockedAddOnVariantIds - Add-on variant ID(s) that must live
     *   and die with the main item: visible in the cart and charged through the main
     *   item's line price, but not removable or re-countable on their own.
     * @returns {string} JSON cart add payload containing the main item and add-ons.
     */
    static createMultiItemCartPayload(
      formData, addOnVariantIds, supplementVariantIds = [], lockedAddOnVariantIds = [], extraOptionVariantIds = []
    ) {
      const mainItem = { properties: {} };
      const payload = { items: [mainItem] };

      formData.forEach((value, name) => {
        if (name === 'id') {
          mainItem.id = value;
          return;
        }

        if (name === 'quantity') {
          mainItem.quantity = Number(value) || 1;
          return;
        }

        if (name === 'selling_plan') {
          mainItem.selling_plan = value;
          return;
        }

        const propertyMatch = name.match(/^properties\[(.+)]$/);
        if (propertyMatch) {
          mainItem.properties[propertyMatch[1]] = value;
          return;
        }

        if (name !== 'form_type' && name !== 'utf8') payload[name] = value;
      });

      if (Object.keys(mainItem.properties).length === 0) delete mainItem.properties;
      if (!mainItem.quantity) mainItem.quantity = 1;

      const uniqueIds = (value) => {
        const list = Array.isArray(value) ? value : [value];
        return [...new Set(list.filter(Boolean).map(String))];
      };

      uniqueIds(addOnVariantIds).forEach((id) => {
        payload.items.push({
          id,
          quantity: mainItem.quantity
        });
      });

      // Locked add-ons and Quick Pro supplements share one bundle id with the
      // main item, which is what lets the cart keep them in step: the roles only
      // differ in how the line is drawn.
      const uniqueLockedAddOns = uniqueIds(lockedAddOnVariantIds);
      const uniqueSupplements = uniqueIds(supplementVariantIds);
      if (uniqueLockedAddOns.length || uniqueSupplements.length) {
        const bundleId = ProductForm.createBundleId();
        const parentVariant = `gid://shopify/ProductVariant/${mainItem.id}`;
        mainItem.properties = mainItem.properties || {};
        mainItem.properties._raceit_bundle_id = bundleId;
        mainItem.properties._raceit_bundle_role = 'parent';
        mainItem.properties._raceit_parent_variant = parentVariant;
        mainItem.properties._raceit_bundle_version = '1';

        const pushChild = (id, role) => {
          payload.items.push({
            id,
            quantity: mainItem.quantity,
            properties: {
              ...(extraOptionVariantIds.map(String).includes(String(id)) ? { _raceit_option_charge: '1' } : {}),
              ...(String(id) === '56873153823106' && mainItem.properties.Bicicleta
                ? { Bicicleta: mainItem.properties.Bicicleta } : {}),
              _raceit_bundle_id: bundleId,
              _raceit_bundle_role: role,
              _raceit_parent_variant: parentVariant,
              _raceit_bundle_version: '1'
            }
          });
        };

        // Both ride as 'component'. The vocabulary is not ours to extend: the
        // Cart Transform merges only 'parent' + 'component', and the validation
        // Function treats any other role on a hidden-addon product as an orphan
        // and refuses the add with "solo se vende dentro de una bicicleta
        // configurada". A separate 'addon' role here did exactly that to every
        // handlebar spacer order until 2026-08-31.
        uniqueLockedAddOns.forEach((id) => pushChild(id, 'component'));
        uniqueSupplements.forEach((id) => pushChild(id, 'component'));
      }

      return JSON.stringify(payload);
    }

    /** A collision-resistant cart-local identifier shared by one configured build. */
    static createBundleId() {
      if (window.crypto?.randomUUID) return `RI-${window.crypto.randomUUID()}`;
      const random = window.crypto?.getRandomValues
        ? window.crypto.getRandomValues(new Uint32Array(2)).join('-')
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      return `RI-${random}`;
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) {
        // The box sits at the top of the form, above every add-on, so on a long
        // build it was off screen and the button seemed to do nothing. Show it
        // right above the button instead, and bring it into view.
        const addToCart = this.querySelector('.product-info__add-to-cart');
        if (addToCart && addToCart.parentElement === this.errorMsg.parentElement
          && this.errorMsg.nextElementSibling !== addToCart) {
          addToCart.before(this.errorMsg);
        }
        this.errorMsg.innerHTML = '';
        const errorArray = Array.isArray(error) ? error : [error];
        errorArray.forEach((err, index) => {
          if (index > 0) this.errorMsg.insertAdjacentHTML('beforeend', '<br>');
          this.errorMsg.insertAdjacentText('beforeend', err);
        });
        const box = this.errorMsg.getBoundingClientRect();
        if (box.top < 0 || box.bottom > window.innerHeight) {
          const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          this.errorMsg.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
        }
      }
    }
  }

  customElements.define('product-form', ProductForm);
}
